<template>
	<div
		class="scroll-fade-container"
		ref="fade-container"
		:class="{ 'scroll-fade-container--tabs': isTabsInterface }"
	>
		<custom-scrollbar
			ref="scroll"
			:autoHide="false"
			class="scroll-fade"
			:class="{
				'fade-top': showTopFade,
				'fade-bottom': showBottomFade,
				'fade-left': showLeftFade,
				'fade-right': showRightFade,
			}"
			:direction="direction"
		>
			<slot name="scrollContent" />
		</custom-scrollbar>
	</div>
</template>

<script lang="ts" setup>
import { ScrollDirection } from "@/types/ui";
import { hideAllPoppers } from "floating-vue";
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

interface Props {
	isTabsInterface?: boolean;
	direction?: ScrollDirection;
}

const { isTabsInterface = false, direction = ScrollDirection.VERTICAL } =
	defineProps<Props>();

const scroll = useTemplateRef<HTMLElement>("scroll");
const scrollEl = ref<HTMLElement | null>(null);
const showTopFade = ref<boolean>(false);
const showBottomFade = ref<boolean>(false);
const showLeftFade = ref<boolean>(false);
const showRightFade = ref<boolean>(false);
const scrollEndCorrection = ref<number>(2); // 2px offset

const fadeContainer = useTemplateRef<HTMLElement>("fade-container");

defineExpose({
	fadeContainer,
});

const updateShadows = (): void => {
	if (!scrollEl.value) return;
	const scrollTop = scrollEl.value!.scrollTop;
	const maxScrollTop =
		scrollEl.value!.scrollHeight - scrollEl.value!.clientHeight;

	// Disable both vertical shadows if there's no vertical scrollable area.
	if (scrollEl.value!.scrollHeight <= scrollEl.value!.clientHeight) {
		showTopFade.value = false;
		showBottomFade.value = false;
	} else {
		showTopFade.value = scrollTop > 0;
		showBottomFade.value =
			scrollTop < maxScrollTop - scrollEndCorrection.value;
	}

	if (
		direction === "horizontal" ||
		scrollEl.value!.scrollWidth >= scrollEl.value!.clientWidth
	) {
		showLeftFade.value = scrollEl.value!.scrollLeft > 0;
		showRightFade.value =
			scrollEl.value!.scrollLeft <
			scrollEl.value!.scrollWidth -
				scrollEl.value!.clientWidth -
				scrollEndCorrection.value;
	}
};

const onScroll = (): void => {
	hideAllPoppers();
	updateShadows();
};

onMounted((): void => {
	// Listener for scrollable area changes & shadows update.
	if (scroll?.value && "scrollEl" in scroll.value) {
		scrollEl.value = scroll?.value.scrollEl as HTMLElement;

		scrollEl.value?.addEventListener("scroll", onScroll);
	}

	// Initial update of shadows on next tick.
	requestAnimationFrame(updateShadows);
});

onBeforeUnmount((): void => {
	if (scrollEl.value) {
		scrollEl.value?.removeEventListener("scroll", onScroll);
	}
});
</script>

<style lang="scss">
// NOTE: here cannot be scoped scss
.scroll-fade {
	position: relative;
	max-height: 100% !important;

	&-container {
		width: 100%;
		height: 100%;

		&--tabs {
			background-image: linear-gradient(
				to top,
				rgba(44, 45, 52, 0.5),
				rgba(60, 61, 67, 0.5)
			);
		}
	}

	&.fade-top {
		mask-image: linear-gradient(to bottom, transparent 0%, black 10%);
	}

	&.fade-bottom {
		mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
	}

	&.fade-left {
		mask-image: linear-gradient(to left, black 90%, transparent 100%);
	}

	&.fade-right {
		mask-image: linear-gradient(to right, black 90%, transparent 100%);
	}

	&.fade-top.fade-bottom {
		mask-image: linear-gradient(
			to bottom,
			transparent 0%,
			black 10%,
			black 90%,
			transparent 100%
		);
	}

	&.fade-left.fade-right {
		mask-image: linear-gradient(
			to right,
			transparent 0%,
			black 10%,
			black 90%,
			transparent 100%
		);
	}
}

.scrollbar {
	&__wrapper {
		width: 100%;
		// IMPORTANT: height is set to 99.5% to allow for the detection of scroll events (e.g. scroll to bottom)
		height: 99.5%;
	}

	&__thumbPlaceholder--horizontal {
		height: 0.375rem;
	}

	&__thumbPlaceholder--vertical {
		right: 0.063rem !important;
		width: 0.375rem;
	}

	&__thumb.scrollbar__thumb--vertical {
		position: relative;
		z-index: 99999;
		border-radius: 0;
		@include background(
			url($path-images + "scrollbar--vertical.avif"),
			// must be 2x 100% due to dynamic resize
			100% 100%,
			center,
			no-repeat
		);
		opacity: 1;
	}
	&__thumb.scrollbar__thumb--horizontal {
		position: relative;
		z-index: 99999;
		border-radius: 0;
		@include background(
			url($path-images + "scrollbar--horizontal.avif"),
			// must be 2x 100% due to dynamic resize
			100% 100%,
			center,
			no-repeat
		);
		opacity: 1;
	}
}
</style>
